import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { USER_CONST } from '@/constant';
import { useChangeReviewerMutation, useTaskDetailQuery } from '@/hook';
import {
  Check,
  Close,
  VBasePopover,
  VButton,
  VFlex,
  VInput,
  VStack,
  cn,
} from '@/package';
import { useAuthStore } from '@/store';
import type { User } from '@/type';

import type { ReviewerHandoffModalProps } from './type';

export const ReviewerHandoffModalContent = ({
  frameSeq,
  message,
  taskID,
  onClose,
}: Pick<
  ReviewerHandoffModalProps,
  'frameSeq' | 'message' | 'taskID' | 'onClose'
>) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { mutateAsync: changeReviewer } = useChangeReviewerMutation();
  const [searchValue, setSearchValue] = useState('');
  const [reviewerID, setReviewerID] = useState<Nullish<User['id']>>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const { data } = useTaskDetailQuery(taskID);
  const userList =
    (data?.userTask
      ?.map(user => user.user)
      .filter(
        _user =>
          _user != null &&
          _user.id !== user.id &&
          (_user?.role === USER_CONST.USER_ROLE.MANAGER ||
            _user?.role === USER_CONST.USER_ROLE.ADMIN ||
            _user?.role === USER_CONST.USER_ROLE.SITE_ADMIN),
      ) as User[]) ?? [];

  const isDisabled = !reviewerID || reviewerID === user.id;
  const handleHandoff = () => {
    if (!reviewerID) return;

    return changeReviewer({
      frameSeq,
      reviewerID,
      taskID,
    })
      .then(() => {
        onClose();
      })
      .catch(err => {
        console.group('[ReviewerHandoffController] error');
        console.warn(err);
        setErrorMessage(err.message);
        console.groupEnd();
      });
  };

  useEffect(() => {
    if (!searchValue) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReviewerID(null);
    }
  }, [searchValue]);

  return (
    <VStack className="w-full">
      <VFlex className="-mt-2" items="center" justify="between">
        <span className="text-body-xl text-gray-900">
          {t('task.reviewerHandoffModalTitle.somethingness')}
        </span>
        <Close
          className="size-6 cursor-pointer text-gray-400"
          role="button"
          onClick={onClose}
        />
      </VFlex>

      <VStack gap={0.5} className="py-4">
        <VStack gap={0.25}>
          <div className="text-body-xl text-gray-900">{message}</div>
          <div className="text-body-lg text-gray-700">
            {t('task.reviewerHandoffModalSearchReviewerPlaceholder.mononymy')}
          </div>
        </VStack>

        <VBasePopover
          className={cn({
            'w-[329px]! bg-white!': true,
            'hidden!': !searchValue,
          })}
          content={() => {
            const list = userList.filter(
              user =>
                searchValue &&
                (user.email.includes(searchValue) ||
                  user.name?.includes(searchValue)),
            );

            return (
              <VStack className="max-h-[168px] overflow-y-auto">
                {list.length ? (
                  list.map(user => {
                    const label =
                      user.name || user.email?.split('@')?.[0] || user.id;
                    const isSelected = reviewerID === user.id;

                    return (
                      <VFlex
                        key={user.id}
                        aria-selected={isSelected}
                        className="rounded-sm px-3 py-2 text-body-md hover:bg-secondary-300/10 aria-selected:bg-secondary-300/20"
                        role="button"
                        gap={0.25}
                        items="center"
                        onClick={() => {
                          setReviewerID(user.id);
                          setSearchValue(label);
                        }}
                      >
                        {isSelected && (
                          <Check className="size-5 text-secondary-300" />
                        )}
                        {label}
                      </VFlex>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 text-body-md text-gray-700">
                    {t('list.emptyLength.washbasin')}
                  </div>
                )}
              </VStack>
            );
          }}
          placement="bottom"
          trigger="focus"
        >
          <VInput
            autoFocus
            placeholder={t(
              'task.reviewerHandoffModalSearchReviewerPlaceholder.mononymy',
            )}
            required
            type="search"
            value={searchValue}
            valid={!errorMessage}
            onChange={e => setSearchValue(e.target.value)}
          />
        </VBasePopover>
      </VStack>

      <VFlex items="center" justify="end" gap={0.25} className="pt-2">
        <VButton outlined size="sm" variant="secondary" onClick={onClose}>
          {t('common.cancel.isocinchonine')}
        </VButton>
        <VButton size="sm" disabled={isDisabled} onClick={handleHandoff}>
          {t('task.reviewerHandoffModalHandOffButtonText.kaladana')}
        </VButton>
      </VFlex>
    </VStack>
  );
};

ReviewerHandoffModalContent.displayName = 'ReviewerHandoffModalContent';
